'use client';

import { FC, ReactNode, HTMLProps } from 'react';
import NextLink, { LinkProps } from 'next/link';
import { determinePathType } from '@/utils';
import { useAuthStore } from '@/store/authStore/authStore';
import { config } from '@/config';
import { useLocale } from '@/hooks/general/useLocale/useLocale';
import { UrlObject } from 'url';

export interface CustomLinkProps
    extends LinkProps,
        Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'as' | 'ref' | 'locale'> {
    children: ReactNode;
    href: LinkProps['href'];
}

export const Link: FC<CustomLinkProps> = ({
    as,
    children,
    href,
    replace,
    scroll,
    shallow,
    passHref,
    ...rest
}) => {
    const { locale } = useLocale();

    const { isLoggedIn } = useAuthStore((state) => ({
        isLoggedIn: state.loggedIn,
    }));

    const getDestinationUrl = (url: LinkProps['href']): string => {
        const urlString = urlToString(url);
        const { isUnavailableToLoggedInUsersPath, isPrivatePath } = determinePathType(urlString);

        const isNonDefaultLocale = locale !== config.defaultLocale;
        const isExternalURL = /^(http|https):\/\//.test(urlString);
        const shouldIncludeLocaleInUrl = isNonDefaultLocale && !isExternalURL;

        const localePrefix = shouldIncludeLocaleInUrl ? `/${locale}` : '';

        if (isLoggedIn && isUnavailableToLoggedInUsersPath) {
            return `${localePrefix}/surveys`;
        } else if (!isLoggedIn && isPrivatePath) {
            return `${localePrefix}/login?to=${urlString}`;
        }
        return `${localePrefix}${urlString}`;
    };

    const destinationHref = getDestinationUrl(href);

    return (
        <NextLink
            as={as}
            href={destinationHref}
            passHref={passHref}
            replace={replace}
            scroll={scroll}
            shallow={shallow}
            {...rest}
        >
            {children}
        </NextLink>
    );
};

export default Link;

const urlToString = (url: LinkProps['href']): string => {
    if (typeof url === 'string') {
        return url;
    }

    if (!url) {
        return '';
    }

    const { pathname, query, hash } = url as UrlObject;

    let path = pathname || '';
    let queryString = '';
    let hashString = '';

    if (query) {
        if (typeof query === 'string') {
            queryString = query.startsWith('?') ? query : `?${query}`;
        } else {
            const params = new URLSearchParams();
            Object.entries(query).flatMap(([key, value]) => {
                if (value !== undefined) {
                    if (Array.isArray(value)) {
                        return value.map((v) => params.append(key, String(v)));
                    } else {
                        params.append(key, String(value));
                    }
                }
                return [];
            });

            queryString = params.toString() ? `?${params.toString()}` : '';
        }
    }

    if (hash) {
        hashString = hash.startsWith('#') ? hash : `#${hash}`;
    }

    return `${path}${queryString}${hashString}`;
};
